import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {
  addTransactionsRequest,
  deleteTransactionsRequest,
  getAllTransactionsRequest,
  getSingleTransactionsRequest,
  updateTransactionsRequest,
} from '../../../api/endpoints/transactions'
import {attachedPaginatedPayload, SHOW_ERROR, SHOW_SUCCESS} from '../../../util'
import {RootState} from '../../store'

function normalizeApiMessage(message: unknown): string {
  if (message == null || message === '') {
    return 'Something went wrong'
  }
  if (typeof message === 'string') {
    return message
  }
  if (Array.isArray(message)) {
    const first = message[0]
    return first == null || first === '' ? 'Something went wrong' : normalizeApiMessage(first)
  }
  if (typeof message === 'object' && message !== null) {
    const values = Object.values(message as Record<string, unknown>).flat()
    const first = values.find((v) => v != null && v !== '')
    if (first !== undefined) {
      return normalizeApiMessage(first)
    }
  }
  return String(message)
}

function mapTransactionErrorMessage(message: string): string {
  if (message === 'Description already exists for this type') {
    return 'Convenience fee already exists and cannot be added again.'
  }
  return message
}

interface transactionState {
  transactions: Array<any>
  transaction: any
  status: string
  error: string | null
  pagination: any
}

export const initialState: transactionState = {
  transactions: [],
  transaction: {},
  pagination: {},
  status: 'idle',
  error: null,
}

export const getTransactionsAction = createAsyncThunk('get/transactions', async (params: any) => {
  try {
    const res: any = await getAllTransactionsRequest(params)
    const {data, success, message} = res.data
    return {
      data,
      success,
      code: 200,
    }
  } catch (error) {
    console.log('get transactions error', error)
    return error
  }
})

export const getSingleTransactionsAction = createAsyncThunk(
  'get/single-transaction',
  async (id: string | number) => {
    try {
      const res: any = await getSingleTransactionsRequest(id)
      const {data, success} = res.data
      return {
        data,
        success,
        code: 200,
      }
    } catch (error) {
      return error
    }
  }
)

export const addTransactionAction = createAsyncThunk(
  'post/transaction',
  async (rData: any, thunkAPI) => {
    try {
      const res: any = await addTransactionsRequest(rData.data)
      const responsePayload = res?.data ?? res?.response?.data

      if (!responsePayload) {
        const msg = mapTransactionErrorMessage(normalizeApiMessage(res?.message))
        SHOW_ERROR(true, msg)
        return thunkAPI.rejectWithValue({message: msg})
      }

      const {data, success, message, errors, error} = responsePayload

      if (success !== true) {
        const msg = mapTransactionErrorMessage(
          normalizeApiMessage(
          (data as any)?.error ?? (data as any)?.errors ?? message ?? errors ?? error
          )
        )
        SHOW_ERROR(true, msg)
        return thunkAPI.rejectWithValue({message: msg})
      }

      rData.SHOW_SUCCESS(true, 'Transaction added successfully')
      rData.navigate('/transaction-types')
      return {
        data,
        success,
        code: 200,
      }
    } catch (error: any) {
      const msg = mapTransactionErrorMessage(
        normalizeApiMessage(
        error?.response?.data?.message ??
          error?.response?.data?.data?.error ??
          error?.message
        )
      )
      SHOW_ERROR(true, msg)
      return thunkAPI.rejectWithValue({message: msg})
    }
  }
)

export const updateTransactionAction = createAsyncThunk(
  'update/transaction',
  async (rData: any, thunkAPI) => {
    try {
      const res: any = await updateTransactionsRequest(rData.data, rData.data.id)
      const responsePayload = res?.data ?? res?.response?.data

      if (!responsePayload) {
        const msg = mapTransactionErrorMessage(normalizeApiMessage(res?.message))
        SHOW_ERROR(true, msg)
        return thunkAPI.rejectWithValue({message: msg})
      }

      const {data, success, message, errors, error} = responsePayload

      if (success !== true) {
        const msg = mapTransactionErrorMessage(
          normalizeApiMessage(
          (data as any)?.error ?? (data as any)?.errors ?? message ?? errors ?? error
          )
        )
        SHOW_ERROR(true, msg)
        return thunkAPI.rejectWithValue({message: msg})
      }

      rData.SHOW_SUCCESS(true, 'Transaction updated successfully')
      rData.navigate('/transaction-types')
      return {
        data,
        success,
        code: 200,
      }
    } catch (error: any) {
      const msg = mapTransactionErrorMessage(
        normalizeApiMessage(
        error?.response?.data?.message ??
          error?.response?.data?.data?.error ??
          error?.message
        )
      )
      SHOW_ERROR(true, msg)
      return thunkAPI.rejectWithValue({message: msg})
    }
  }
)

export const deleteTransactionAction = createAsyncThunk(
  'delete/transaction',
  async (params: any) => {
    try {
      const res: any = await deleteTransactionsRequest(params.id)
      // const {data, success} = res.data
      if (res?.data?.success) {
        SHOW_SUCCESS(true, 'Transaction deleted successfully')
        params.handleClose()
        return {
          id: params.id,
          data: res.data.data,
          success: res.data.success,
          code: 200,
        }
      } else {
        // console.log('data', res.response.data)
        SHOW_ERROR(true, res.response.data.message[0])
        params.handleClose()
        return {
          success: false,
          code: 400,
        }
      }
    } catch (error) {
      return error
    }
  }
)

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getTransactionsAction.pending, (state, action) => {
      state.status = 'loading'
    })
    builder.addCase(getTransactionsAction.fulfilled, (state, action: any) => {
      state.status = 'success'
      state.transactions = action.payload.data
      state.pagination = attachedPaginatedPayload(action.payload.data)
    })
    builder.addCase(getTransactionsAction.rejected, (state, action) => {
      state.status = 'failed'
    })

    builder.addCase(getSingleTransactionsAction.pending, (state, action) => {
      state.status = 'loading'
    })
    builder.addCase(getSingleTransactionsAction.fulfilled, (state, action: any) => {
      state.status = 'success'
      state.transaction = action.payload.data
    })
    builder.addCase(getSingleTransactionsAction.rejected, (state, action) => {
      state.status = 'failed'
    })

    builder.addCase(addTransactionAction.pending, (state, action) => {
      state.status = 'loading'
    })
    builder.addCase(addTransactionAction.fulfilled, (state, action: any) => {
      state.status = 'success'
    })
    builder.addCase(addTransactionAction.rejected, (state, action) => {
      state.status = 'failed'
    })

    builder.addCase(updateTransactionAction.pending, (state, action) => {
      state.status = 'loading'
    })
    builder.addCase(updateTransactionAction.fulfilled, (state, action: any) => {
      state.status = 'success'
    })
    builder.addCase(updateTransactionAction.rejected, (state, action) => {
      state.status = 'failed'
    })

    builder.addCase(deleteTransactionAction.pending, (state, action) => {
      state.status = 'loading'
    })
    builder.addCase(deleteTransactionAction.fulfilled, (state, action: any) => {
      if (action.payload.code === 200) {
        state.status = 'success'
        state.transactions = state.transactions.filter((item: any) => item.id !== action.payload.id)
      } else {
        state.status = 'failed'
      }
    })
    builder.addCase(deleteTransactionAction.rejected, (state, action) => {
      state.status = 'failed'
    })
  },
})

export const transactionSelector = (state: RootState) => state.transactions.transactions
export const singleTransactionSelector = (state: RootState) => state.transactions.transaction
export const transactionStatusSelector = (state: RootState) => state.transactions.status

export default transactionSlice.reducer
